// Array utils

type ValueFunc<T> = (a: T) => number;

export function arrayEqual(array1: unknown[], array2: unknown[]): boolean {
  if (array1.length !== array2.length) return false;

  for (let i = 0; i < array1.length; i++) {
    const compare = Array.isArray(array1[i])
      ? Array.isArray(array2[i])
        ? arrayEqual(array1[i] as unknown[], array2[i] as unknown[])
        : false
      : array1 === array2;

    if (!compare) {
      return false;
    }
  }
  return true;
}

export function arrayMax<T>(array: T[], valueFunc?: ValueFunc<T>): number {
  // Returns *index* of max element according to valueFunc
  // Return -1 if array empty
  // If multiple indices reach the max, returns the first
  // If valueFunc thinks an index should be skipped, return -Inf for that index

  let indexOfMax = -1;
  let valueOfMax = -Infinity;

  for (let i = 0; i < array.length; i++) {
    const value = valueFunc ? valueFunc(array[i]) : (array as number[])[i];
    if (valueOfMax < value) {
      indexOfMax = i;
      valueOfMax = value;
    }
  }
  return indexOfMax;
}

export function arrayMin<T>(array: T[], valueFunc?: ValueFunc<T>): number {
  const newValueFunc = valueFunc ? (a: T) => -valueFunc(a) : (a: T) => -a;
  return arrayMax(array, newValueFunc);
}

// Array manipulation utils
export const swap = <T>(arr: T[], idx0: number, idx1: number): T[] =>
  arr.map((a: T, idx) => (idx === idx0 ? arr[idx1] : idx === idx1 ? arr[idx0] : a));

export const moveToTop = <T>(arr: T[], idx: number): T[] =>
  idx > 0 ? [arr[idx], ...arr.slice(0, idx), ...arr.slice(idx + 1)] : arr;
export const moveToBottom = <T>(arr: T[], idx: number): T[] =>
  idx < arr.length - 1 ? [...arr.slice(0, idx), ...arr.slice(idx + 1), arr[idx]] : arr;
export const moveUp = <T>(arr: T[], idx: number): T[] => (idx > 0 ? swap(arr, idx, idx - 1) : arr);
export const moveDown = <T>(arr: T[], idx: number): T[] =>
  idx < arr.length - 1 ? swap(arr, idx, idx + 1) : arr;

export type MoveDirection = "top" | "up" | "down" | "bottom";

export const moveByDirection = <T>(arr: T[], idx: number, direction: MoveDirection): T[] => {
  if (direction === "up") return moveUp(arr, idx);
  if (direction === "down") return moveDown(arr, idx);
  if (direction === "top") return moveToTop(arr, idx);
  return moveToBottom(arr, idx);
};

export const replaceElement = <T>(arr: T[], idx: number, a: T): T[] =>
  arr
    .slice(0, idx)
    .concat([a])
    .concat(arr.slice(idx + 1));

export const remove = <T>(arr: T[], idx: number): T[] =>
  arr.slice(0, idx).concat(arr.slice(idx + 1));

export const makeUnique = <T>(arr: T[], toString: (t: T) => string = (t) => "" + t): T[] => {
  const seen: Record<string, boolean> = {};
  const unique: T[] = [];

  arr.forEach((a) => {
    const aa: string = toString(a);
    if (seen[aa]) return;
    seen[aa] = true;
    unique.push(a);
  });

  return unique;
};

export const removeNullUndefined = <T>(array: (T | null | undefined)[]): T[] =>
  array.filter((a) => a !== null && a !== undefined) as T[];

/* Function to flatten arrays, recursively */
type NestedArray<T> = Array<NestedArray<T> | T>;
function flattenPartial<T>(array: T[][]): T[] {
  return ([] as T[]).concat(...array);
}
export function flatten<T>(array: NestedArray<T>): T[] {
  return flattenPartial(array.map((t) => (Array.isArray(t) ? flatten(t) : [t])));
}

// Backwards functions

// Find last element that satisfies findFunction.
export function findLast<T>(array: T[], findFunction: (t: T) => boolean): T | undefined {
  for (let i = array.length - 1; i >= 0; i--) {
    const e = array[i];
    if (findFunction(e)) return e;
  }
  return undefined;
}
export function findLastIndex<T>(array: T[], findFunction: (t: T) => boolean): number | undefined {
  for (let i = array.length - 1; i >= 0; i--) {
    const e = array[i];
    if (findFunction(e)) return i;
  }
  return undefined;
}

// Equivalent of Python's range function
export function range(start: number, end?: number, step = 1): number[] {
  if (end !== undefined) {
    const arr = [];
    for (let i = start; i < end; i += step) {
      arr.push(i);
    }
    return arr;
  } else {
    return range(0, start, step);
  }
}
